/**
 * Offline action queue — check-ins done with no signal are stored in IndexedDB
 * and replayed automatically when connectivity returns (on `online` event and
 * on app start). The caller receives a synthetic success so the UI updates
 * optimistically; cached booking lists are patched so the checked-in state
 * survives offline re-renders.
 */
import { CACHE_STORE, QUEUE_STORE, idbPut, idbAll, idbDelete, isNetworkFailure } from './offlineCache';

const QUEUEABLE = [
  { re: /\/organizations\/[^/]+\/bookings\/([^/]+)\/check-in$/, kind: 'check-in' },
];

let flushing = false;
let instanceRef = null;

export async function getQueueCount() {
  try {
    return (await idbAll(QUEUE_STORE)).length;
  } catch {
    return 0;
  }
}

function matchQueueable(config) {
  if (!config || (config.method || '').toLowerCase() !== 'post') return null;
  return QUEUEABLE.find((q) => q.re.test(config.url || '')) || null;
}

// Best-effort: mark the booking checked-in inside every cached bookings list
// so offline re-renders keep showing the right state.
async function patchCachedBookings(bookingId) {
  try {
    const entries = await idbAll(CACHE_STORE);
    const nowIso = new Date().toISOString();
    for (const e of entries) {
      if (!e.key.includes('/bookings')) continue;
      let changed = false;
      const patch = (b) => {
        if (b && b.id === bookingId) {
          changed = true;
          return { ...b, checkInStatus: 'checked_in', checkedIn: true, checkInAt: nowIso };
        }
        return b;
      };
      if (Array.isArray(e.data)) e.data = e.data.map(patch);
      else if (e.data && Array.isArray(e.data.bookings)) e.data = { ...e.data, bookings: e.data.bookings.map(patch) };
      if (changed) await idbPut(CACHE_STORE, e);
    }
  } catch {
    /* best-effort */
  }
}

export function attachOfflineQueue(axiosInstance) {
  instanceRef = axiosInstance;
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const q = matchQueueable(error.config);
      if (q && isNetworkFailure(error)) {
        try {
          const entry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            url: error.config.url,
            body: error.config.data || null,
            kind: q.kind,
            ts: Date.now(),
          };
          await idbPut(QUEUE_STORE, entry);
          const m = (error.config.url || '').match(/\/bookings\/([^/]+)\/check-in$/);
          if (m) patchCachedBookings(m[1]);
          const pending = await getQueueCount();
          window.dispatchEvent(new CustomEvent('bf-offline-queued', { detail: { pending } }));
          return {
            data: { success: true, alreadyCheckedIn: false, checkInAt: new Date().toISOString(), queuedOffline: true },
            status: 200,
            statusText: 'OK (queued offline)',
            headers: {},
            config: error.config,
            queuedOffline: true,
          };
        } catch {
          /* fall through to the original error */
        }
      }
      return Promise.reject(error);
    }
  );

  window.addEventListener('online', () => setTimeout(() => flushQueue(), 1500));
  setTimeout(() => flushQueue(), 4000); // catch anything left over from a previous session
}

export async function flushQueue() {
  if (flushing || !instanceRef) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  flushing = true;
  let synced = 0;
  let skipped = 0;
  try {
    const items = (await idbAll(QUEUE_STORE)).sort((a, b) => a.ts - b.ts);
    for (const item of items) {
      try {
        let body;
        try {
          body = item.body ? JSON.parse(item.body) : undefined;
        } catch {
          body = item.body || undefined;
        }
        await instanceRef.post(item.url, body);
        await idbDelete(QUEUE_STORE, item.id);
        synced += 1;
      } catch (err) {
        if (err?.response) {
          // The server answered (conflict, already checked in, validation…) —
          // retrying will never succeed, so drop it.
          await idbDelete(QUEUE_STORE, item.id);
          skipped += 1;
        } else {
          break; // still offline — keep the rest for the next flush
        }
      }
    }
  } finally {
    flushing = false;
  }
  if (synced || skipped) {
    const pending = await getQueueCount();
    window.dispatchEvent(new CustomEvent('bf-offline-synced', { detail: { synced, skipped, pending } }));
  }
}
